/** Tipos das respostas da API do Comercial (B1.1) e rótulos de exibição. */
export type Status = 'ativo' | 'inativo';
export type SituacaoIe = 'ativa' | 'inativa' | 'isento' | 'nao_contribuinte';
export type SituacaoCadastral = 'ativa' | 'suspensa' | 'inapta' | 'baixada' | 'nula';

export interface Entidade {
  id: string;
  cliente_id: string;
  /** atributo editável (0022); a entidade padrão do Cliente é a PRINCIPAL */
  tipo: 'matriz' | 'filial' | 'unidade' | 'nao_informado';
  /** código CLI-NNNN/BBBB (null = colisão de estabelecimento pendente de decisão) */
  codigo?: string | null;
  principal?: boolean;
  principal_provisoria?: boolean;
  /** usada em Oportunidade, Proposta ou Pedido (a transferência só vale sem negócios) */
  com_negocios?: boolean;
  /** veio da migração da planilha (correções exigem justificativa) */
  migrado?: boolean;
  cnpj: string;
  razao_social: string;
  nome_fantasia: string | null;
  ie: string | null;
  ie_situacao: SituacaoIe;
  situacao_cadastral: SituacaoCadastral;
  cep: string;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string;
  uf: string;
  status: Status;
  atualizado_em: string;
}
export interface Contato {
  id: string;
  /** preenchido só em contato migrado: edição exige justificativa */
  origem_sistema?: string | null;
  cliente_id: string;
  entidade_id: string;
  nome: string;
  cargo: string | null;
  departamento: string | null;
  email: string | null;
  telefone: string | null;
  whatsapp: string | null;
  principal: boolean;
  papel: string | null;
  observacao: string | null;
  status: Status;
  atualizado_em: string;
}
export const CAMPOS_PERFIL = ['perfil_empresa', 'porte_estimado', 'solucoes_maior', 'solucoes_secundaria', 'potencial_cross_sell', 'temas_email', 'prioridade_marketing'] as const;
export type CampoPerfil = (typeof CAMPOS_PERFIL)[number];
export const ROTULOS_PERFIL: Record<CampoPerfil, string> = {
  perfil_empresa: 'Perfil da empresa',
  porte_estimado: 'Porte estimado',
  solucoes_maior: 'Soluções de maior aderência',
  solucoes_secundaria: 'Soluções de aderência secundária',
  potencial_cross_sell: 'Potencial de cross-sell',
  temas_email: 'Temas de e-mail recomendados',
  prioridade_marketing: 'Prioridade de marketing',
};
export interface Indicacao {
  tipo: 'cliente' | 'contato' | 'parceiro' | 'livre';
  id?: string;
  nome: string;
}
export interface Cliente extends Record<CampoPerfil, string | null> {
  id: string;
  codigo: string;
  nome: string;
  marca: string | null;
  segmento: string | null;
  subsegmento: string | null;
  status: Status;
  responsavel_id: string;
  responsavel_nome: string | null;
  origem: string;
  indicacao_tipo: Indicacao['tipo'] | null;
  indicacao_id: string | null;
  indicacao_nome: string | null;
  observacoes: string | null;
  atualizado_em: string;
  entidades: Entidade[];
  contatos: Contato[];
  migrado?: boolean;
  /** CLI legados e códigos anteriores a transferências, pesquisáveis */
  aliases?: { codigo: string; entidade_id: string | null; origem: string; entidade_codigo: string | null }[];
  /** Resumo comercial (B2). */
  resumo?: { oportunidades_abertas: number; oportunidades_ganhas: number; ganhas_integrais?: number; ganhas_parciais?: number; oportunidades: number; propostas?: number; pedidos: number; ordens?: number; ultima_interacao: string | null; ultima_interacao_tipo?: string | null; ultima_interacao_autor?: string | null };
}
export interface ClienteResumo {
  id: string;
  codigo: string;
  nome: string;
  marca: string | null;
  segmento: string | null;
  subsegmento: string | null;
  status: Status;
  responsavel_nome: string;
  /** entidade PRINCIPAL (a chave "matriz" permanece por compatibilidade) */
  matriz: { razao_social: string; cnpj: string; cidade: string; uf: string; codigo?: string | null; tipo?: Entidade['tipo']; principal_provisoria?: boolean } | null;
  filiais: number;
  entidades?: number;
  encontrado_por?: { tipo: 'cliente' | 'entidade' | 'legado'; codigo: string; entidade?: string | null } | null;
  /** P3-08/P3-09: unidades ativas do Cliente */
  unidades_ativas?: number;
}
export interface Opcoes {
  papeis_contato?: string[];
  papeis_configurados?: boolean;
  origens: string[];
  origens_configuradas: boolean;
  responsaveis: { id: string; nome: string }[];
  ufs: { sigla: string; nome: string }[];
}
export interface ContatoConsolidado extends Contato {
  cliente: { id: string; codigo: string; nome: string; segmento: string | null; subsegmento: string | null };
  entidade: { id: string; tipo: Entidade['tipo']; codigo?: string | null; razao_social: string; nome_fantasia: string | null; cnpj: string };
}
export interface ContatoDetalhe extends Contato {
  cliente: { id: string; codigo: string; nome: string; segmento: string | null; subsegmento: string | null; status: Status };
  entidade: Entidade;
}

export type TipoEntidade = Entidade['tipo'];
export const ROTULO_TIPO_ENTIDADE: Record<Entidade['tipo'], string> = { matriz: 'Matriz', filial: 'Filial', unidade: 'Unidade', nao_informado: 'Não informado' };
export const TIPOS_ENTIDADE = Object.keys(ROTULO_TIPO_ENTIDADE) as Entidade['tipo'][];
/** Tipo sugerido pelo estabelecimento do CNPJ (/0001 → Matriz), sempre editável. */
export const tipoSugerido = (cnpj: string): Entidade['tipo'] => (cnpj.replace(/\D/g, '').slice(8, 12) === '0001' ? 'matriz' : 'filial');
/** "CLI-0018/0052 · Filial · SP" (código, tipo e UF da entidade). */
export const rotuloEntidade = (e: Pick<Entidade, 'codigo' | 'tipo' | 'uf' | 'cnpj'>) => [e.codigo ?? `código a definir (${e.cnpj})`, ROTULO_TIPO_ENTIDADE[e.tipo], e.uf].filter(Boolean).join(' · ');
export const ROTULO_IE: Record<SituacaoIe, string> = { ativa: 'Ativa', inativa: 'Inativa', isento: 'Isento', nao_contribuinte: 'Não contribuinte' };
export const ROTULO_SITUACAO_CADASTRAL: Record<SituacaoCadastral, string> = { ativa: 'Ativa', suspensa: 'Suspensa', inapta: 'Inapta', baixada: 'Baixada', nula: 'Nula' };
export const rotuloStatus = (s: Status, feminino = false) => (s === 'ativo' ? (feminino ? 'Ativa' : 'Ativo') : feminino ? 'Inativa' : 'Inativo');
const ROTULO_TIPO_IND: Record<Indicacao['tipo'], string> = { cliente: 'Cliente', contato: 'Contato', parceiro: 'Parceiro comercial', livre: 'Nome livre' };
export const rotuloTipoIndicacao = (t: Indicacao['tipo']) => ROTULO_TIPO_IND[t];
export const textoIndicacao = (i?: Indicacao | null) => (i ? `${i.nome} (${ROTULO_TIPO_IND[i.tipo].toLowerCase()})` : '—');
export const indicacaoDe = (c: Pick<Cliente, 'indicacao_tipo' | 'indicacao_id' | 'indicacao_nome'>): Indicacao | null =>
  c.indicacao_tipo ? { tipo: c.indicacao_tipo, id: c.indicacao_id ?? undefined, nome: c.indicacao_nome ?? '' } : null;
/** Entidade PRINCIPAL do Cliente (0022: separada de Matriz). */
export const matrizDe = (c: Pick<Cliente, 'entidades'>) => c.entidades.find((e) => e.principal) ?? c.entidades.find((e) => e.tipo === 'matriz') ?? c.entidades[0];
/** Endereço no formato da DEMO-03 ("logradouro, número — complemento — bairro — cidade/UF — CEP"), sem partes vazias. */
export function enderecoTexto(e: Pick<Entidade, 'logradouro' | 'numero' | 'complemento' | 'bairro' | 'cidade' | 'uf' | 'cep'>): string {
  const rua = [e.logradouro, e.numero].filter(Boolean).join(', ');
  return [rua, e.complemento, e.bairro, `${e.cidade}/${e.uf}`, e.cep && `CEP ${e.cep}`].filter(Boolean).join(' — ');
}
